import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environment';
import { ConfigurationService } from 'src/app/core/services/api/configuration.service';

interface ICalendarDateItem {
  month: {
    en: string;
    he: string;
  };
  year: number;
  monthNum: number;
}

@Component({
    selector: 'que-date-picker',
    imports: [CommonModule, OverlayModule],
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatePickerComponent {

  @Input() formatDate?: string | null;
  @Input() customDate?: Date | null;
  @Input('rangeNumberOfMonthForward') rangeNumberOfMonthForward?: number | null;
  @Input() minDate?: Date | null;
  @Input() maxDate?: Date | null;
  @Output('rangeChanged') rangePickedEmitter = new EventEmitter<{ startDate?: Date | null, endDate?: Date | null }>();
  readonly positonArea = [{
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  }] as ConnectedPosition[];
  monthDic: { en: string, he: string }[] = [
    { en: "January", he: "ינואר" },
    { en: "February", he: "פברואר" },
    { en: "March", he: "מרץ" },
    { en: "April", he: "אפריל" },
    { en: "May", he: "מאי" },
    { en: "June", he: "יוני" },
    { en: "July", he: "יולי" },
    { en: "August", he: "אוגוסט" },
    { en: "September", he: "ספטמבר" },
    { en: "October", he: "אוקטובר" },
    { en: "November", he: "נובמבר" },
    { en: "December", he: "דצמבר" },
  ];

  valueOfMonthSelect!: { en: string, he: string };
  valueOfYearSelect!: number;
  yearNames: number[] = [];
  selectedMonth!: ICalendarDateItem;
  isYearsToSelectOpen: boolean = false;

  constructor(private ref: ChangeDetectorRef) { }

  ngOnInit(): void {
    for (let i = 2000; i <= new Date().getFullYear() + 20; i++) {
      this.yearNames.push(i);
    }
    if (!this.customDate)
      this.customDate = new Date();
    this.selectedMonth = this.parseDate(this.customDate);
    this.valueOfMonthSelect = this.selectedMonth.month;
    this.valueOfYearSelect = this.selectedMonth.year;
  }

  selectYear(year: string) {
    const selectedYear = +year;

    if (this.isYearDisabled(selectedYear)) {
      return;
    }

    this.valueOfYearSelect = selectedYear;
    this.selectedMonth.year = this.valueOfYearSelect;
    this.isYearsToSelectOpen = false;
  }

  selectMonthFromSelect(month: { en: string; he: string; }) {
    if (this.isMonthDisabled(month)) {
      return;
    }

    this.valueOfMonthSelect = month;
    this.selectedMonth.month = month;
    this.selectedMonth.monthNum = this.monthDic.indexOf(month) + 1;

    this.rangePickedEmitter.emit({
      startDate: new Date(this.selectedMonth.year, this.selectedMonth.monthNum - 1, 1),
      endDate: new Date(this.selectedMonth.year, this.selectedMonth.monthNum, 0)
    });
  }
  
  parseDate(date: Date | string | null | undefined): ICalendarDateItem {
    const parsedDate = date instanceof Date ? date : new Date(date ?? new Date());

    if (isNaN(parsedDate.getTime())) {
      const now = new Date();
      return {
        month: this.monthDic[now.getMonth()],
        monthNum: now.getMonth() + 1,
        year: now.getFullYear(),
      };
    }

    return {
      month: this.monthDic[parsedDate.getMonth()],
      monthNum: parsedDate.getMonth() + 1,
      year: parsedDate.getFullYear(),
    };
  }

  isYearDisabled(year: number): boolean {
    if (this.minDate && (year < this.minDate?.getFullYear())) return true;
    if (this.maxDate && (year > this.maxDate.getFullYear())) return true;

    return false;
  }
  private getMonthStart(year: number, monthNum: number): Date {
    return new Date(year, monthNum - 1, 1);
  }

  private getMonthEnd(year: number, monthNum: number): Date {
    return new Date(year, monthNum, 0);
  }

  isMonthDisabled(month: { en: string; he: string }): boolean {
    const monthNum = this.monthDic.indexOf(month) + 1;
    const year = this.selectedMonth?.year ?? this.valueOfYearSelect;

    const monthStart = this.getMonthStart(year, monthNum);
    const monthEnd = this.getMonthEnd(year, monthNum);

    if (this.minDate && (monthEnd < this.stripTime(this.minDate))) return true;

    if (this.maxDate && (monthStart > this.stripTime(this.maxDate))) return true;

    return false;
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}